import { useEffect, useMemo, useState } from 'react';
import Seo from '../../components/Seo';
import AnimatedEyebrow from '../../components/AnimatedEyebrow';
import ProductsFiltersSection from '../../components/products-page/ProductsFiltersSection';
import ProductsGridSection from '../../components/products-page/ProductsGridSection';
import ProductsSupportSection from '../../components/products-page/ProductsSupportSection';
import ShortcutsSection from '../../components/ShortcutsSection';
import { DEFAULT_SHORTCUTS } from '../../components/shortcutsData';
import PageLoader from '../../components/PageLoader';
import { useI18n } from '../../context/I18nContext';
import { getProductBrand } from '../../lib/productBrand';
import { localizePath } from '../../lib/localizedRoutes';
import { buildProductSearchText, filterAndRankByQuery } from '../../lib/search';
import { absoluteUrl, getSiteUrl } from '../../lib/seo';
import { getImageUrl, normalizeImageList } from '../../lib/imageData';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useUrlSyncedFilters from '../../hooks/useUrlSyncedFilters';

const PRODUCTS_PER_PAGE = 6;
const ALL_FILTER_VALUE = '';
const SEARCH_DEBOUNCE_MS = 300;
const MACHINES_PAGE_CACHE = new Map();

function isPublicProduct(item) {
  return String(item?.productType || '').trim() !== 'unclassified';
}

async function fetchJsonOrThrow(url) {
  const response = await fetch(url, { credentials: 'include' });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error('Request failed');
  }
  return data;
}

function sanitizePage(raw) {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? String(n) : '1';
}

function getMachinesPageCacheKey(language) {
  return String(language || '').trim() || 'default';
}

export default function ProductsPage() {
  const { language, t } = useI18n();
  const cacheKey = getMachinesPageCacheKey(language);
  const cachedData = MACHINES_PAGE_CACHE.get(cacheKey) || null;
  const siteUrl = getSiteUrl();
  const title = language === 'fr' ? 'Catalogue machines' : 'Machines catalog';
  const description =
    language === 'fr'
      ? 'Parcourez le catalogue de machines ETC et filtrez par marque, type et recherche rapide.'
      : 'Browse the ETC machine catalog and filter by brand, type, and quick search.';
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [brandRegistry, setBrandRegistry] = useState([]);
  const [isLoading, setIsLoading] = useState(() => !cachedData);

  // ---- URL-synced filter state ------------------------------------------------
  // `q` is the live (instant) input value; `committedQuery` is the debounced
  // value used for filtering. Brand and productType are committed on click.
  //
  // IMPORTANT: the field name is `productType` (not `type`) because the rest
  // of the app reads `?productType=…` from `router.query` (see `TitleBar`,
  // `HomeHeroSection`, `pages/machines/categories.js`, `pages/machines/[id].js`).
  // Using a different key here would silently break those callers.
  const {
    values: { q: liveQuery, brand, productType, page: urlPage },
    setField,
    resetAll,
    isReady: filtersReady,
  } = useUrlSyncedFilters({
    fields: {
      q: { defaultValue: ALL_FILTER_VALUE },
      brand: { defaultValue: ALL_FILTER_VALUE },
      productType: { defaultValue: ALL_FILTER_VALUE },
      page: {
        defaultValue: '1',
        parse: sanitizePage,
        serialize: sanitizePage,
      },
    },
    // Reset clears the user's in-category search/brand/page, but keeps the
    // active `productType` so the user stays in the category they navigated
    // to. Going back to the unfiltered catalog requires clicking the
    // "All products" link in the breadcrumb.
    resetKeys: ['q', 'brand', 'page'],
  });

  const committedQuery = useDebouncedValue(liveQuery, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (cachedData) {
      setProducts(cachedData.products);
      setProductTypes(cachedData.productTypes);
      setBrandRegistry(cachedData.brandRegistry);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    Promise.all([
      fetchJsonOrThrow(`/api/products?lang=${language}`),
      fetchJsonOrThrow('/api/product-types').catch(() => []),
      fetchJsonOrThrow('/api/brands').catch(() => []),
    ])
      .then(([productsData, typesData, brandsData]) => {
        if (!isMounted) return;

        const nextProducts = (Array.isArray(productsData) ? productsData : []).filter(isPublicProduct);
        const nextProductTypes = Array.isArray(typesData) ? typesData : [];
        const nextBrandRegistry = Array.isArray(brandsData) ? brandsData : [];

        MACHINES_PAGE_CACHE.set(cacheKey, {
          products: nextProducts,
          productTypes: nextProductTypes,
          brandRegistry: nextBrandRegistry,
        });

        setProducts(nextProducts);
        setProductTypes(nextProductTypes);
        setBrandRegistry(nextBrandRegistry);
      })
      .catch(() => {
        if (!isMounted) return;
        setProducts([]);
        setProductTypes([]);
        setBrandRegistry([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [cacheKey, cachedData, language]);

  // Push the debounced query into the URL when it settles.
  useEffect(() => {
    if (!filtersReady) return;
    if (committedQuery !== liveQuery) {
      setField('q', committedQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedQuery, filtersReady]);

  // ---- Derived options and filtered list --------------------------------------
  const brandOptions = useMemo(
    () => [ALL_FILTER_VALUE, ...new Set(products.map((item) => getProductBrand(item)).filter(Boolean))],
    [products],
  );

  const productTypeMetaByKey = useMemo(
    () =>
      new Map(
        productTypes.map((item) => [
          item.key,
          {
            label: item?.labelI18n?.[language] || item.label || item.key,
            fields: new Map(
              Array.isArray(item.fields)
                ? item.fields.map((field) => [
                    field.key,
                    {
                      label: field?.labelI18n?.[language] || field.label || field.key,
                      unit: field.unit || '',
                    },
                  ])
                : [],
            ),
          },
        ]),
      ),
    [productTypes, language],
  );

  const selectedType = productType || ALL_FILTER_VALUE;
  const selectedBrand = brand || ALL_FILTER_VALUE;

  const filteredProducts = useMemo(() => {
    const searchMatched = filterAndRankByQuery(products, committedQuery, (item) => {
      const typeKey = String(item.productType || 'unclassified');
      const typeMeta = productTypeMetaByKey.get(typeKey) || null;
      return buildProductSearchText(item, typeMeta);
    });
    return searchMatched.filter((item) => {
      const typeKey = String(item.productType || 'unclassified');
      if (selectedType && typeKey !== selectedType) return false;
      if (selectedBrand && getProductBrand(item) !== selectedBrand) return false;
      return true;
    });
  }, [products, selectedType, selectedBrand, committedQuery, productTypeMetaByKey]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)),
    [filteredProducts.length],
  );

  const parsedPage = Math.max(1, parseInt(urlPage, 10) || 1);
  const currentPage = Math.min(parsedPage, totalPages);

  // Snap the URL page back to 1 (or the new last page) when filters change.
  useEffect(() => {
    if (!filtersReady) return;
    if (currentPage !== parsedPage) {
      setField('page', String(currentPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, parsedPage, filtersReady]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const filteredTypeCounts = useMemo(
    () =>
      filteredProducts.reduce((counts, item) => {
        const typeKey = String(item?.productType || 'unclassified').trim() || 'unclassified';
        counts[typeKey] = (counts[typeKey] || 0) + 1;
        return counts;
      }, {}),
    [filteredProducts],
  );

  const visibleStart = filteredProducts.length === 0 ? 0 : (currentPage - 1) * PRODUCTS_PER_PAGE + 1;
  const visibleEnd = filteredProducts.length === 0 ? 0 : visibleStart + paginatedProducts.length - 1;
  const productItemListJsonLd = paginatedProducts.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: title,
        numberOfItems: filteredProducts.length,
        itemListElement: paginatedProducts.map((product, index) => {
          const productId = String(product?._id || product?.id || '').trim();
          const firstImage = normalizeImageList(product?.images)[0];
          const imageUrl = firstImage ? absoluteUrl(getImageUrl(firstImage), siteUrl) : undefined;

          return {
            '@type': 'ListItem',
            position: visibleStart + index,
            name: product?.name || productId,
            url: absoluteUrl(localizePath(`/machines/${productId}`, language), siteUrl),
            image: imageUrl,
          };
        }),
      }
    : null;
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: language === 'fr' ? 'Accueil' : 'Home',
        item: absoluteUrl('/', siteUrl),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title,
        item: absoluteUrl('/machines', siteUrl),
      },
    ],
  };
  const introTitle = language === 'fr' ? 'Choisir la bonne machine' : 'Choose the right machine';
  const introTitleLine1 = language === 'fr' ? 'TROUVEZ VOTRE PROCHAIN' : 'FIND YOUR NEXT';
  const introTitleLine2 = language === 'fr' ? 'MACHINE' : 'MACHINE';
  const introText =
    language === 'fr'
      ? 'Utilisez les filtres pour comparer les marques, les types et les usages, puis explorez les catégories les plus adaptées à votre activité.'
      : 'Use the filters to compare brands, types, and use cases, then explore the categories that fit your operation best.';

  // ---- Render -----------------------------------------------------------------
  if (isLoading) {
    return (
      <>
        <Seo
          title={title}
          description={description}
          jsonLd={[breadcrumbJsonLd, productItemListJsonLd].filter(Boolean)}
          keywords={{
            en: ['machines catalog', 'forklifts', 'pallet trucks', 'warehouse equipment', 'industrial machinery'],
            fr: ['catalogue machines', 'chariots élévateurs', 'transpalettes', 'matériel de manutention', 'matériel industriel'],
          }}
        />
        <PageLoader label={t('productsPage.loading', 'Loading products...')} />
      </>
    );
  }

  return (
    <section className="relative min-h-screen overflow-x-hidden bg-[#0a0a0c] text-white selection:bg-[var(--dash-main)] selection:text-black">
      <Seo
        title={title}
        description={description}
        jsonLd={[breadcrumbJsonLd, productItemListJsonLd].filter(Boolean)}
        keywords={{
          en: ['machines catalog', 'forklifts', 'pallet trucks', 'warehouse equipment', 'industrial machinery'],
          fr: ['catalogue machines', 'chariots élévateurs', 'transpalettes', 'matériel de manutention', 'matériel industriel'],
        }}
      />
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" />
      </div>

      <main className="container relative z-10 mx-auto px-6 pb-20 lg:px-10">
        <section className="pt-0 pb-14 lg:pt-0 lg:pb-20">
          <div className="mx-auto max-w-5xl text-center text-white">
            <AnimatedEyebrow className="mx-auto mb-6 w-fit">
              {language === 'fr' ? 'Catalogue machines' : 'Machine catalog'}
            </AnimatedEyebrow>
            <h2 className="mx-auto mt-6 max-w-4xl text-4xl font-light uppercase leading-[0.94] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
              <span className="block text-white">{introTitleLine1}</span>
              <span className="mt-2 block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
                {introTitleLine2}
              </span>
            </h2>
            <p className="mx-auto mt-5 max-w-4xl text-sm font-light leading-relaxed text-slate-300 md:text-base">
              {introText}
            </p>  
          </div>
        </section>
      <ProductsFiltersSection
        query={liveQuery}
        onQueryChange={(value) => setField('q', value)}
        selectedBrand={selectedBrand}
        onBrandChange={(value) => setField('brand', value || ALL_FILTER_VALUE)}
        onReset={resetAll}
        hasActiveTypeFilter={selectedType !== ALL_FILTER_VALUE}
        brandOptions={brandOptions}
        visibleStart={visibleStart}
        visibleEnd={visibleEnd}
        totalCount={filteredProducts.length}
      />
      <ProductsGridSection
        products={paginatedProducts}
        isLoading={false}
        productTypeMetaByKey={productTypeMetaByKey}
        brandRegistry={brandRegistry}
        totalCountByType={filteredTypeCounts}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(next) => setField('page', String(next))}
      />
      <ProductsSupportSection />
      </main>
      <ShortcutsSection items={DEFAULT_SHORTCUTS} excludeCurrent />
    </section>
  );
}
